export const HSE_RISK_CATEGORY_KEYS = [
  "health_safety",
  "environmental",
  "quality",
  "financial",
  "operational",
  "reputational",
  "legal_compliance",
  "climate_related",
];

export const HSE_RISK_HANDLING_KEYS = [
  "register_and_sja",
  "sja_first",
  "register_only",
  "custom",
];

export const HSE_SETUP_WARN_KEYS = ["responsible", "verneombud"];
export const HSE_SETUP_INFO_KEYS = ["categories", "ai_key"];

function asBool(value, fallback = false) {
  if (value === true || value === false) return value;
  if (value === "true") return true;
  if (value === "false") return false;
  return fallback;
}

function asUserId(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
}

function asUserIds(value) {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  const ids = [];
  value.forEach((item) => {
    const id = asUserId(typeof item === "object" ? item?.id : item);
    if (id && !seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  });
  return ids;
}

export function normalizeHseSafetyRepSettings(data = {}) {
  return {
    safety_representative_user_id: asUserId(
      data.verneombud_user_id ?? data.safety_representative_user_id
    ),
    deputy_user_ids: asUserIds(data.deputy_user_ids),
  };
}

export function normalizeHseRiskSettings(data = {}) {
  const risk = data.risk && typeof data.risk === "object" ? data.risk : data;
  const handling = HSE_RISK_HANDLING_KEYS.includes(risk.handling)
    ? risk.handling
    : "register_and_sja";
  const categories = Array.isArray(risk.default_categories)
    ? risk.default_categories.filter((key) => HSE_RISK_CATEGORY_KEYS.includes(key))
    : [];

  return {
    handling,
    handling_notes: String(risk.handling_notes || "").trim(),
    responsible_user_id: asUserId(risk.responsible_user_id),
    assessor_user_ids: asUserIds(risk.assessor_user_ids || data.assessor_user_ids),
    skip_requires_reason: asBool(risk.skip_requires_reason, true),
    default_categories: categories,
    default_matrix: risk.default_matrix || data.default_matrix || null,
  };
}

export function normalizeHseAiSettings(data = {}) {
  const ai = data.ai && typeof data.ai === "object" ? data.ai : data;
  return {
    suggestions_enabled: asBool(ai.enabled ?? ai.suggestions_enabled, false),
    default_discipline_id: asUserId(ai.default_discipline_id),
    provider: ai.provider || "chatgpt",
    configured: asBool(ai.configured, false),
  };
}

export function snapshotHseSafetyRepSettings(data) {
  return JSON.stringify(normalizeHseSafetyRepSettings(data));
}

export function snapshotHseRiskSettings(data) {
  const next = normalizeHseRiskSettings(data);
  return JSON.stringify({
    handling: next.handling,
    handling_notes: next.handling_notes,
    responsible_user_id: next.responsible_user_id,
    assessor_user_ids: next.assessor_user_ids,
    skip_requires_reason: next.skip_requires_reason,
  });
}

export function snapshotHseAiSettings(data) {
  const next = normalizeHseAiSettings(data);
  return JSON.stringify({
    suggestions_enabled: next.suggestions_enabled,
    default_discipline_id: next.default_discipline_id,
  });
}

export function toHseSafetyRepPayload(data) {
  const next = normalizeHseSafetyRepSettings(data);
  return {
    verneombud_user_id: next.safety_representative_user_id,
    deputy_user_ids: next.deputy_user_ids,
  };
}

export function toHseRiskPayload(data) {
  const next = normalizeHseRiskSettings(data);
  return {
    assessor_user_ids: next.assessor_user_ids,
    risk: {
      handling: next.handling,
      handling_notes: next.handling_notes,
      responsible_user_id: next.responsible_user_id,
      assessor_user_ids: next.assessor_user_ids,
      skip_requires_reason: next.skip_requires_reason,
    },
  };
}

export function toHseAiPayload(data) {
  const next = normalizeHseAiSettings(data);
  return {
    ai: {
      enabled: next.suggestions_enabled,
    },
  };
}

export function normalizeHseRiskCategory(raw = {}) {
  const id = asUserId(raw.id);
  return {
    id,
    slug: String(raw.slug || "").trim(),
    name: String(raw.name || "").trim(),
    position: Number.isInteger(Number(raw.position)) ? Number(raw.position) : 0,
    system_default: asBool(raw.system_default, false),
    active: raw.active !== false,
    company_id: asUserId(raw.company_id),
  };
}

export function normalizeHseRiskCategories(raw) {
  const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
  return list.map((item) => normalizeHseRiskCategory(item));
}

function setupSource(raw = {}) {
  if (raw.setup && typeof raw.setup === "object" && (raw.setup.flags || raw.setup.issues)) {
    return raw.setup;
  }
  return raw;
}

export function normalizeHseSetupStatus(raw = {}) {
  const source = setupSource(raw);
  const flags = source.flags && typeof source.flags === "object" ? source.flags : {};
  const issues = Array.isArray(source.issues)
    ? source.issues
        .filter((item) => item && item.key)
        .map((item) => ({
          key: String(item.key),
          severity: item.severity === "info" ? "info" : "warn",
          message: item.message || "",
        }))
    : [];

  const resolvedFlags = {
    responsible_missing:
      flags.responsible_missing === true || issues.some((item) => item.key === "responsible"),
    verneombud_missing:
      flags.verneombud_missing === true || issues.some((item) => item.key === "verneombud"),
    categories_missing:
      flags.categories_missing === true || issues.some((item) => item.key === "categories"),
    ai_enabled: asBool(flags.ai_enabled, false),
    ai_key_missing: flags.ai_key_missing === true || issues.some((item) => item.key === "ai_key"),
    ai_configured: asBool(flags.ai_configured, false),
  };

  if (issues.length === 0 && flags.responsible_missing == null && raw.responsible_user_id !== undefined) {
    resolvedFlags.responsible_missing = !asUserId(raw.responsible_user_id);
  }
  if (
    issues.length === 0 &&
    flags.verneombud_missing == null &&
    (raw.verneombud_user_id !== undefined || raw.safety_representative_user_id !== undefined)
  ) {
    resolvedFlags.verneombud_missing = !asUserId(
      raw.verneombud_user_id ?? raw.safety_representative_user_id
    );
  }

  const resolvedIssues =
    issues.length > 0
      ? issues
      : [
          resolvedFlags.responsible_missing ? { key: "responsible", severity: "warn", message: "" } : null,
          resolvedFlags.verneombud_missing ? { key: "verneombud", severity: "warn", message: "" } : null,
          resolvedFlags.categories_missing ? { key: "categories", severity: "info", message: "" } : null,
          resolvedFlags.ai_key_missing ? { key: "ai_key", severity: "info", message: "" } : null,
        ].filter(Boolean);

  return {
    ready: resolvedIssues.every((item) => item.severity !== "warn"),
    issues: resolvedIssues,
    flags: resolvedFlags,
  };
}

export function hseSetupIssues(settingsOrSetup = {}, { includeInfo = false } = {}) {
  if (settingsOrSetup == null || typeof settingsOrSetup !== "object") return [];
  const setup = normalizeHseSetupStatus(settingsOrSetup);
  return setup.issues
    .filter((item) => includeInfo || item.severity === "warn")
    .map((item) => item.key);
}

export function viewerCanSeeHseSetupBanner(user, settings, isAdmin) {
  if (isAdmin) return true;
  const userId = asUserId(user?.id);
  if (!userId) return false;
  const safety = normalizeHseSafetyRepSettings(settings || {});
  return (
    userId === asUserId(settings?.responsible_user_id) ||
    userId === asUserId(settings?.verneombud_user_id) ||
    userId === safety.safety_representative_user_id ||
    safety.deputy_user_ids.includes(userId)
  );
}
